/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useState } from "react";
import { getOptionsChain } from "../../api/api";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import PageBackButton from "../../components/PageBackButton";

const POPULAR_SYMBOLS = ["AAPL", "TSLA", "SPY", "QQQ", "AMZN", "MSFT", "NVDA", "META", "GOOG", "GLD"];

// Forex symbol hints
const FOREX_HINTS = {
  EURUSD: "FXE", USDJPY: "FXY", GBPUSD: "FXB", AUDUSD: "FXA",
  USDCAD: "FXC", USDCHF: "FXF", XAUUSD: "GLD", XAGUSD: "SLV",
};

export default function OptionsPage() {
  const [symbol, setSymbol] = useState("");
  const [expiration, setExpiration] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [optionsData, setOptionsData] = useState(null);
  const [selectedExpIdx, setSelectedExpIdx] = useState(0);

  const fetchOptions = async (sym) => {
    const targetSymbol = sym || symbol;
    if (!targetSymbol) return;

    try {
      setLoading(true);
      setError(null);
      setOptionsData(null);
      setSelectedExpIdx(0);

      const params = { symbol: targetSymbol };
      if (expiration) params.expiration_date = expiration;

      const res = await getOptionsChain(params);

      if (res?.error) {
        setError(res.error);
        return;
      }

      if (!res?.chains || res.chains.length === 0) {
        setError(`No options data found for ${targetSymbol}`);
        return;
      }

      setOptionsData(res);
    } catch (e) {
      const msg = e?.response?.data?.error || e.message || "Failed to load options data";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const currentChain = optionsData?.chains?.[selectedExpIdx] || null;
  const calls = currentChain?.calls || [];
  const puts = currentChain?.puts || [];

  // Build summary from current chain
  const summary = currentChain ? {
    putCallRatio: currentChain.put_call_ratio,
    putCallOiRatio: currentChain.put_call_oi_ratio,
    totalCalls: currentChain.calls_count || calls.length,
    totalPuts: currentChain.puts_count || puts.length,
    daysToExpiry: currentChain.days_to_expiry,
  } : null;

  const fmtNum = (v, dec = 2) => (v != null ? Number(v).toFixed(dec) : "-");
  const fmtInt = (v) => (v != null ? Number(v).toLocaleString() : "-");
  const fmtPct = (v) => (v != null ? (Number(v) * 100).toFixed(1) + "%" : "-");

  const OptionTable = ({ data, type }) => (
    <Card className="p-0 overflow-hidden">
      <div className={`p-3 ${type === "calls" ? "bg-green-500/10" : "bg-red-500/10"} border-b border-border/40`}>
        <h2 className={`font-medium ${type === "calls" ? "text-green-400" : "text-red-400"}`}>
          {type === "calls" ? "Calls" : "Puts"} ({data.length})
        </h2>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-secondary/50">
            <tr>
              <th className="text-left p-3">Strike</th>
              <th className="text-right p-3">Last</th>
              <th className="text-right p-3">Bid</th>
              <th className="text-right p-3">Ask</th>
              <th className="text-right p-3">Change</th>
              <th className="text-right p-3">Volume</th>
              <th className="text-right p-3">Open Int</th>
              <th className="text-right p-3">IV</th>
              <th className="text-center p-3">ITM</th>
            </tr>
          </thead>
          <tbody>
            {data.map((o, i) => (
              <tr key={i} className={`border-t border-border/40 hover:bg-secondary/20 ${o.inTheMoney ? "bg-primary/5" : ""}`}>
                <td className="p-3 font-medium">{fmtNum(o.strike)}</td>
                <td className="p-3 text-right">{fmtNum(o.lastPrice)}</td>
                <td className="p-3 text-right">{fmtNum(o.bid)}</td>
                <td className="p-3 text-right">{fmtNum(o.ask)}</td>
                <td className={`p-3 text-right ${(o.change ?? 0) >= 0 ? "text-green-400" : "text-red-400"}`}>
                  {fmtNum(o.change)}
                </td>
                <td className="p-3 text-right">{fmtInt(o.volume)}</td>
                <td className="p-3 text-right">{fmtInt(o.openInterest)}</td>
                <td className="p-3 text-right">{fmtPct(o.impliedVolatility)}</td>
                <td className="p-3 text-center">{o.inTheMoney ? "✓" : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );

  return (
    <div className="max-w-7xl mx-auto space-y-4">
      <div className="flex items-start gap-3">
        <PageBackButton label="Back to Research" to="/research" preferHistory={false} hideLabelOnMobile />
        <div>
          <h1 className="text-2xl font-semibold">Options</h1>
          <p className="text-sm text-textSecondary">
            Options chain data — view calls, puts, and key metrics for any symbol.
            Forex pairs are automatically mapped to currency ETFs (e.g. EURUSD → FXE).
          </p>
        </div>
      </div>

      {/* Search & Filters */}
      <Card className="p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-sm mb-1">Symbol</label>
            <Input
              placeholder="e.g. AAPL, SPY, EURUSD"
              value={symbol}
              onChange={(e) => setSymbol(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === "Enter" && fetchOptions()}
            />
            {FOREX_HINTS[symbol] && (
              <p className="text-xs text-textSecondary mt-1">
                Will use ETF proxy: {FOREX_HINTS[symbol]}
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm mb-1">Expiration Date (optional)</label>
            <Input
              type="date"
              value={expiration}
              onChange={(e) => setExpiration(e.target.value)}
            />
          </div>
          <div className="flex items-end">
            <Button onClick={() => fetchOptions()} disabled={loading}>
              {loading ? "Loading..." : "Search"}
            </Button>
          </div>
        </div>

        <div>
          <label className="block text-sm mb-2">Popular Symbols</label>
          <div className="flex flex-wrap gap-2">
            {POPULAR_SYMBOLS.map((s) => (
              <Button
                key={s}
                type="button"
                variant={symbol === s ? "default" : "secondary"}
                size="sm"
                disabled={loading}
                onClick={() => {
                  setSymbol(s);
                  fetchOptions(s);
                }}
              >
                {s}
              </Button>
            ))}
          </div>
        </div>
      </Card>

      {/* Loading */}
      {loading && (
        <Card className="p-6 text-center text-textSecondary">
          <div className="animate-pulse">Fetching options data... this may take a few seconds</div>
        </Card>
      )}

      {/* Error */}
      {error && !loading && (
        <Card className="p-6 text-center">
          <p className="text-sm text-red-400">{error}</p>
        </Card>
      )}

      {/* Results */}
      {optionsData && !loading && (
        <>
          {/* Header info */}
          <Card className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div>
                <span className="text-sm text-textSecondary">Symbol: </span>
                <span className="font-semibold">{optionsData.symbol}</span>
                {optionsData.is_forex_proxy && (
                  <span className="text-xs text-textSecondary ml-2">
                    (proxy for {optionsData.original_symbol})
                  </span>
                )}
              </div>
              {optionsData.current_price && (
                <div>
                  <span className="text-sm text-textSecondary">Price: </span>
                  <span className="font-semibold">${fmtNum(optionsData.current_price)}</span>
                </div>
              )}
              <div>
                <span className="text-sm text-textSecondary">Expirations: </span>
                <span className="font-semibold">{optionsData.available_expirations?.length || 0}</span>
              </div>
            </div>
          </Card>

          {/* Expiration selector tabs */}
          {optionsData.chains.length > 1 && (
            <div className="flex flex-wrap gap-2">
              {optionsData.chains.map((chain, idx) => (
                <Button
                  key={chain.expiration}
                  variant={selectedExpIdx === idx ? "default" : "secondary"}
                  size="sm"
                  onClick={() => setSelectedExpIdx(idx)}
                >
                  {chain.expiration}
                  {chain.days_to_expiry != null && (
                    <span className="ml-1 text-xs opacity-70">({chain.days_to_expiry}d)</span>
                  )}
                </Button>
              ))}
            </div>
          )}

          {/* Summary cards */}
          {summary && (
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
              <Card className="p-4">
                <div className="text-xs text-textSecondary">Expiration</div>
                <div className="text-lg font-bold">{currentChain?.expiration || "-"}</div>
                {summary.daysToExpiry != null && (
                  <div className="text-xs text-textSecondary">{summary.daysToExpiry} days</div>
                )}
              </Card>
              <Card className="p-4">
                <div className="text-xs text-textSecondary">P/C Ratio (Vol)</div>
                <div className="text-lg font-bold">{summary.putCallRatio?.toFixed(3) || "-"}</div>
              </Card>
              <Card className="p-4">
                <div className="text-xs text-textSecondary">P/C Ratio (OI)</div>
                <div className="text-lg font-bold">{summary.putCallOiRatio?.toFixed(3) || "-"}</div>
              </Card>
              <Card className="p-4">
                <div className="text-xs text-textSecondary">Calls</div>
                <div className="text-lg font-bold text-green-400">{summary.totalCalls}</div>
              </Card>
              <Card className="p-4">
                <div className="text-xs text-textSecondary">Puts</div>
                <div className="text-lg font-bold text-red-400">{summary.totalPuts}</div>
              </Card>
            </div>
          )}

          {/* Options tables */}
          {calls.length > 0 && <OptionTable data={calls} type="calls" />}
          {puts.length > 0 && <OptionTable data={puts} type="puts" />}

          {calls.length === 0 && puts.length === 0 && (
            <Card className="p-6 text-center text-textSecondary">
              No options contracts found for this expiration
            </Card>
          )}
        </>
      )}

      {/* Empty state when no search */}
      {!loading && !error && !optionsData && (
        <Card className="p-8 text-center">
          <div className="text-4xl mb-3">📊</div>
          <h3 className="text-lg font-medium mb-1">Search for Options</h3>
          <p className="text-sm text-textSecondary">
            Enter a symbol above or click a popular symbol to view the options chain.
            Supports stocks (AAPL, SPY), ETFs (GLD), and forex pairs (EURUSD → FXE proxy).
          </p>
        </Card>
      )}
    </div>
  );
}
